import React, { useState, useEffect } from "react";
import { Button, Row, Col } from "react-bootstrap";
import ActivityList from "../Activities/ActivityList/ActivityList.jsx";
import { formatDate } from "../../utils/auxiliarFunctions.js";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./Calendar.css";

const DAYS_TO_SHOW = 18; // Cantidad de días visibles en pantalla

const Calendar = ({ activities, isAdmin }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [visibleIndex, setVisibleIndex] = useState(0);
  const [daysToShow, setDaysToShow] = useState(DAYS_TO_SHOW);

  const getDaysInMonth = (month, year) => {
    const date = new Date(year, month, 1);
    const days = [];
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    return days;
  };

  useEffect(() => {
    const today = new Date();
    const days = getDaysInMonth(selectedMonth, selectedYear);
    
    // Encuentra el índice del día actual en el array de días del mes
    const todayIndex = days.findIndex((day) => 
      day.toDateString() === today.toDateString()
    );
  
    // Si encontramos el día actual, ajustamos el visibleIndex para que esté en la pantalla
    if (todayIndex !== -1) {
      const rowIndex = Math.floor(todayIndex / DAYS_TO_SHOW) * DAYS_TO_SHOW;
      setVisibleIndex(rowIndex);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    const updateDaysToShow = () => {
      const container = document.querySelector(".calendar-container");
      if (container) {
        const containerWidth = container.offsetWidth;
        const dayWidth = 68; // Ancho de los botones day tomando en cuenta el gap
        const calculatedDays = Math.floor(containerWidth / dayWidth) - 2;
        setDaysToShow(calculatedDays < 6 ? 6 : calculatedDays);
      }
    };

    updateDaysToShow();
    window.addEventListener("resize", updateDaysToShow);
    return () => window.removeEventListener("resize", updateDaysToShow);
  }, []);

  const handleMonthClick = (month) => {
    setSelectedMonth(month);
    setVisibleIndex(0);
  };

  const handleDateClick = (date) => {
    setSelectedDate(formatDate(date));
  };

  const renderMonthButtons = () => {
    const monthNames = [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];

    return (
      <Row className="d-flex justify-content-center mb-3">
        {monthNames.map((month, index) => (
          <Col key={index} className="d-flex justify-content-center p-1">
            <Button
              variant="outline-primary"
              onClick={() => handleMonthClick(index)}
              className={`month-button ${selectedMonth === index ? "active" : ""}`}
            >
              {month}
            </Button>
          </Col>
        ))}
      </Row>
    );
  };

  const renderDaysOfMonth = () => {
    const days = getDaysInMonth(selectedMonth, selectedYear);

    const visibleDays = daysToShow <= 6 ? days : days.slice(visibleIndex, visibleIndex + daysToShow);

    return (
      <div
        className={`calendar-container ${
          daysToShow <= 6 ? "scrollable" : ""
        }`}
      >
        {!(daysToShow <= 6) && (
          <Button
            variant="outline-primary"
            onClick={() => setVisibleIndex(Math.max(0, visibleIndex - daysToShow))}
            disabled={visibleIndex === 0}
            className="nav-button"
          >
            <ChevronLeft />
          </Button>
        )}

        <div className="calendar-days">
          {visibleDays.map((day) => {
            const formattedDate = formatDate(day);
            const hasActivity = activities[formattedDate]?.length > 0;
            const isToday = day.toDateString() === new Date().toDateString();
            const dayName = day.toLocaleDateString("es-ES", { weekday: "short" });

            return (
              <Button
                key={day.toString()}
                className={`day-button ${
                  hasActivity ? "has-activity" : ""
                } ${selectedDate === formattedDate ? "selected" : ""} ${
                  isToday ? "today" : ""
                }`}
                onClick={() => handleDateClick(day)}
              >
                <div className="day-name">{dayName}</div>
                <div className="day-number">{day.getDate()}</div>
              </Button>
            );
          })}
        </div>

        {!(daysToShow <= 6) && (
          <Button
            variant="outline-primary"
            onClick={() =>
              setVisibleIndex(
                Math.min(days.length - daysToShow, visibleIndex + daysToShow)
              )
            }
            disabled={visibleIndex + daysToShow >= days.length}
            className="nav-button"
          >
            <ChevronRight />
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
      {renderMonthButtons()}
      {renderDaysOfMonth()}
      <ActivityList selectedDate={selectedDate} activities={activities[selectedDate] || []} isAdmin={isAdmin} />
    </>
  );
};

export default Calendar;

